import { useState } from "react";

import { toast } from "react-hot-toast";

import { guardarCotizacion, crearNuevaVersion } from "../../../utils/cotizacionesGuardadas";

/**
 * Hook para guardar cotizaciones desde el cotizador.
 * Recolecta inputs + outputs y los persiste en la tabla cotizaciones.
 */
export const useSaveCotizacionHandler = ({
  // Auth
  userId,
  pais,
  nombre: usuarioNombre,
  apellido: usuarioApellido,
  clienteId: autoClienteId,
  clienteNombre: autoClienteNombre,
  // Inputs
  inventarioData,
  isProgrammaticSelected,
  frecuencia,
  plazo,
  duracion,
  horasSeleccionadas,
  codigoGenerado,
  forceDollars,
  // Discount
  discountType,
  discountPercentage,
  manualDiscountAmount,
  finalAmount,
  // Outputs (from useCalculadoraCalcs)
  ascensoresSeleccionados,
  totemsSeleccionados,
  coworkSeleccionados,
  esperaSeleccionados,
  edificiosUnicosTotal,
  audienciaSeleccionadaTotal,
  audienciaSeleccionadaAscensor,
  audienciaSeleccionadaTotems,
  audienciaSeleccionadaCowork,
  audienciaSeleccionadaEspera,
  salidasAscensores,
  salidasTotems,
  salidasCowork,
  salidasEspera,
  salidasTotales,
  impactosTotalAscensores,
  impactosTotalTotems,
  impactosTotalCowork,
  impactosTotalEspera,
  impactosTotales,
  inversionAscensoresFinal,
  inversionTotemsFinal,
  inversionCoworkFinal,
  inversionEsperaFinal,
  inversionFinalTotal,
  tarifaFriaAscensores,
  tarifaFriaTotems,
  tarifaFriaCowork,
  tarifaFriaEspera,
  tarifaFriaTotal,
  cpmAscensoresFinal,
  cpmTotemsFinal,
  cpmCoworkFinal,
  cpmEsperaFinal,
  cpmTotalFinal,
  descuentoGlobal,
  horasFinal,
  // Contactos OTS por circuito
  contactosCampañaAscensores,
  contactosCampañaTotems,
  contactosCampañaCowork,
  contactosCampañaEspera,
  // Impactos efectivos Poisson (objeto { ascensores, totems, cowork, espera, total })
  impactosEfectivosPoisson,
  // Nombres edificios seleccionados (arrays de strings)
  nombresEdificiosSeleccionadosAscensores,
  nombresEdificiosSeleccionadosTotems,
  nombresEdificiosSeleccionadosCowork,
  nombresEdificiosSeleccionadosEspera,
  // Edit mode (versioning)
  cotizacionGuardadaId,
  navigate,
}) => {
  const [isSaving, setIsSaving] = useState(false);
  const [showGuardarModal, setShowGuardarModal] = useState(false);

  const buildCotizacionData = (nombreCotizacion, clienteIdOverride, clienteNombreOverride) => {
    // Building IDs by circuit (para re-cargar en cotizador al editar)
    const keyMap = {
      ascensores: "edificiosSeleccionadosAscensores",
      totems: "edificiosSeleccionadosTotems",
      cowork: "edificiosSeleccionadosCowork",
      espera: "edificiosSeleccionadosEspera",
    };
    const getEdificioIds = (circuit) => {
      const arr = inventarioData?.[keyMap[circuit]];
      if (!Array.isArray(arr) || arr.length === 0) return "";
      return arr.join(",");
    };

    const joinNames = (arr) => (Array.isArray(arr) && arr.length > 0 ? arr.join(",") : null);

    return {
      // Identificación
      codigo_generado: codigoGenerado,
      nombre_cotizacion: nombreCotizacion || null,
      pais,
      usuario_id: userId,
      usuario_nombre: usuarioNombre,
      usuario_apellido: usuarioApellido,
      // Cliente
      cliente_id: clienteIdOverride || autoClienteId || null,
      cliente_nombre: clienteNombreOverride || autoClienteNombre || null,
      // Estado
      estado: "borrador",
      version: 1,
      version_grupo: codigoGenerado,
      eliminada: false,
      // Inputs
      is_programmatic_selected: isProgrammaticSelected,
      frecuencia: frecuencia ? Number(frecuencia) : null,
      plazo,
      duracion,
      horas: horasSeleccionadas?.horas || 0,
      horas_final: horasFinal || 0,
      intervalos: horasSeleccionadas?.intervalos || [],
      options: horasSeleccionadas?.options || [],
      moneda_dolar: forceDollars || false,
      // Building IDs (inputs para edición futura)
      edificios_ids_ascensores: getEdificioIds("ascensores"),
      edificios_ids_totems: getEdificioIds("totems"),
      edificios_ids_cowork: getEdificioIds("cowork"),
      edificios_ids_espera: getEdificioIds("espera"),
      // Discount inputs
      discount_type: discountType || null,
      discount_percentage: discountPercentage || null,
      manual_discount_amount: manualDiscountAmount || null,
      final_amount: finalAmount || null,
      // Outputs - Edificios/Pantallas
      ascensores_seleccionados: ascensoresSeleccionados,
      totems_seleccionados: totemsSeleccionados,
      cowork_seleccionados: coworkSeleccionados,
      espera_seleccionados: esperaSeleccionados,
      edificios_unicos_total: edificiosUnicosTotal,
      // Outputs - Audiencia
      audiencia_seleccionada_total: audienciaSeleccionadaTotal,
      audiencia_seleccionada_ascensor: audienciaSeleccionadaAscensor,
      audiencia_seleccionada_totems: audienciaSeleccionadaTotems,
      audiencia_seleccionada_cowork: audienciaSeleccionadaCowork,
      audiencia_seleccionada_espera: audienciaSeleccionadaEspera,
      // Outputs - Salidas
      salidas_ascensores: salidasAscensores,
      salidas_totems: salidasTotems,
      salidas_cowork: salidasCowork,
      salidas_espera: salidasEspera,
      salidas_totales: salidasTotales,
      // Outputs - Impactos (VAC)
      impactos_total_ascensores: impactosTotalAscensores,
      impactos_total_totems: impactosTotalTotems,
      impactos_total_cowork: impactosTotalCowork,
      impactos_total_espera: impactosTotalEspera,
      impactos_totales: impactosTotales,
      // Outputs - Contactos OTS por circuito
      contactos_campana_ascensores: contactosCampañaAscensores,
      contactos_campana_totems: contactosCampañaTotems,
      contactos_campana_cowork: contactosCampañaCowork,
      contactos_campana_espera: contactosCampañaEspera,
      // Outputs - Impactos efectivos Poisson
      impactos_efectivos_ascensores: impactosEfectivosPoisson?.ascensores || 0,
      impactos_efectivos_totems: impactosEfectivosPoisson?.totems || 0,
      impactos_efectivos_cowork: impactosEfectivosPoisson?.cowork || 0,
      impactos_efectivos_espera: impactosEfectivosPoisson?.espera || 0,
      impactos_efectivos_total: impactosEfectivosPoisson?.total || 0,
      // Outputs - Inversión
      inversion_ascensores_final: inversionAscensoresFinal,
      inversion_totems_final: inversionTotemsFinal,
      inversion_cowork_final: inversionCoworkFinal,
      inversion_espera_final: inversionEsperaFinal,
      inversion_final_total: inversionFinalTotal,
      // Outputs - Tarifa
      tarifa_fria_ascensores: tarifaFriaAscensores,
      tarifa_fria_totems: tarifaFriaTotems,
      tarifa_fria_cowork: tarifaFriaCowork,
      tarifa_fria_espera: tarifaFriaEspera,
      tarifa_fria_total: tarifaFriaTotal,
      // Outputs - CPM
      cpm_ascensores_final: cpmAscensoresFinal,
      cpm_totems_final: cpmTotemsFinal,
      cpm_cowork_final: cpmCoworkFinal,
      cpm_espera_final: cpmEsperaFinal,
      cpm_total_final: cpmTotalFinal,
      // Outputs - Descuento
      descuento_total_final: descuentoGlobal,
      // Nombres edificios seleccionados (texto para PDF sin re-query)
      nombres_edificios_seleccionados_ascensores: joinNames(nombresEdificiosSeleccionadosAscensores),
      nombres_edificios_seleccionados_totems: joinNames(nombresEdificiosSeleccionadosTotems),
      nombres_edificios_cowork: joinNames(nombresEdificiosSeleccionadosCowork),
      nombres_edificios_espera: joinNames(nombresEdificiosSeleccionadosEspera),
    };
  };

  const guardarInternal = async (nombreCotizacion, clienteIdOverride, clienteNombreOverride, shouldNavigate) => {
    setIsSaving(true);
    try {
      const data = buildCotizacionData(nombreCotizacion, clienteIdOverride, clienteNombreOverride);

      let result;
      if (cotizacionGuardadaId) {
        result = await crearNuevaVersion(cotizacionGuardadaId, data);
        toast.success(`Nueva versión v${result.version} guardada`);
      } else {
        result = await guardarCotizacion(data);
        toast.success("Cotización guardada");
      }

      setShowGuardarModal(false);

      if (shouldNavigate && navigate) {
        navigate("/admin/mis-cotizaciones");
      }

      return result;
    } catch (err) {
      toast.error("Error al guardar cotización");
      throw err;
    } finally {
      setIsSaving(false);
    }
  };

  const handleGuardarCotizacion = (nombreCotizacion, clienteIdOverride, clienteNombreOverride) =>
    guardarInternal(nombreCotizacion, clienteIdOverride, clienteNombreOverride, true);

  const handleGuardarSilencioso = (nombreCotizacion, clienteIdOverride, clienteNombreOverride) =>
    guardarInternal(nombreCotizacion, clienteIdOverride, clienteNombreOverride, false);

  return {
    isSaving,
    showGuardarModal,
    setShowGuardarModal,
    handleGuardarCotizacion,
    handleGuardarSilencioso,
  };
};
